// Sticky, always-visible horizontal scrollbar for Tabulator tables.
//
// Our tables set no fixed height — vertical scrolling is page-level and the
// table grows to fit every row. Tabulator's own horizontal scrollbar lives at
// the bottom of `.tabulator-tableholder` (the row-manager element), which on a
// tall table sits far below the fold. So when columns overflow the viewport
// there's no on-screen cue that more columns exist to the right, and on a
// table whose width happens to match the viewport it looks like nothing
// scrolls at all.
//
// This installs a thin proxy scrollbar that:
//   * is `position: sticky; bottom: 0` inside the table container, so it stays
//     pinned to the bottom of the viewport while the table is in view and
//     scrolls away once you reach the table's end (the "moves with vertical
//     scroll" behaviour);
//   * mirrors the tableholder's scroll position bidirectionally;
//   * only appears when the columns actually overflow.
//
// The proxy is driven by SimpleBar (`autoHide: false`) — the same
// `permanent-scrollbar` mechanism used elsewhere — rather than a styled native
// scrollbar. Native scrollbars auto-hide when idle on macOS/iOS even with
// `-webkit-appearance: none`, which defeats the whole point (the cue must be
// permanent). SimpleBar overlays a custom, never-hiding thumb on top of native
// scrolling, so the bar drives the real tableholder via `scrollLeft` and
// composes with `installHorizontalScrollPreservation` (remote mode) for free.

import SimpleBar from 'simplebar';
import { simplebarAriaLabel } from '../../javascript/lib/library_locales';

const HIDDEN_CLASS = 'amba-tabulator-hscroll--hidden';

export class StickyHorizontalScrollbar {
  // `getScrollEl` is a thunk rather than a stored reference because the
  // row-manager element isn't guaranteed to exist yet at construction and is
  // re-read defensively elsewhere in the controller (see
  // `installHorizontalScrollPreservation`).
  //
  // `anchorEl` is the `.tabulator` element. The bar is inserted as its *next
  // sibling*, NOT a child: Tabulator turns the element it's handed into
  // `.tabulator` directly, and that element carries `overflow: hidden` and is
  // only as tall as the table. A bar nested inside it would be clipped and its
  // `position: sticky` would resolve against `.tabulator` (which never
  // scrolls) instead of the page, so it could never float into the viewport.
  // As a sibling its sticky context is the page scroller, and its containing
  // block spans the full table height.
  //
  // `scrollTable(left)` performs the actual horizontal scroll of the table. It
  // must move the body *and* the header directly, not just set `scrollLeft` on
  // the holder: when a filter popup is open `installFilterPopupScrollGuard`
  // swallows the holder's `scroll` event in the capture phase, so the
  // header-sync listener Tabulator binds to that event never runs and the
  // columns would drift out of alignment. The controller wires this to
  // Tabulator's own row + column scroll calls.
  constructor(getScrollEl, anchorEl, scrollTable) {
    this.getScrollEl = getScrollEl;
    this.scrollTable = scrollTable;
    // Re-entrancy guard: writing `scrollLeft` fires a `scroll` event, so
    // each handler would echo back into the other without this flag.
    this._syncing = false;

    this.element = document.createElement('div');
    this.element.className = `amba-tabulator-hscroll ${HIDDEN_CLASS}`;
    // Decorative proxy of a control that already exists on the table itself;
    // hide it from the accessibility tree.
    this.element.setAttribute('aria-hidden', 'true');

    // Its width is set to the table's `scrollWidth` so the proxy gets a
    // scrollbar of the right proportions.
    this.spacer = document.createElement('div');
    this.spacer.className = 'amba-tabulator-hscroll__spacer';
    this.element.appendChild(this.spacer);
    anchorEl.insertAdjacentElement('afterend', this.element);

    // SimpleBar moves the spacer into its own content wrapper; that wrapper is
    // the element that actually scrolls, so all sync targets it rather than
    // `this.element`.
    this.simpleBar = new SimpleBar(this.element, {
      autoHide: false,
      ariaLabel: simplebarAriaLabel(),
    });
    this.proxyScrollEl = this.simpleBar.getScrollElement();

    this.onProxyScroll = this.onProxyScroll.bind(this);
    this.onTableScroll = this.onTableScroll.bind(this);
    this.refresh = this.refresh.bind(this);

    this.proxyScrollEl.addEventListener('scroll', this.onProxyScroll);

    const scrollEl = this.getScrollEl();
    if (scrollEl) scrollEl.addEventListener('scroll', this.onTableScroll);

    // Column widths, data changes and viewport resizes all change the
    // overflow. A ResizeObserver on the scroll element catches content- and
    // viewport-driven size changes in one shot. Guarded for jsdom, where the
    // constructor is undefined.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.refresh);
      if (scrollEl) this.resizeObserver.observe(scrollEl);
    }

    this.refresh();
  }

  onProxyScroll() {
    if (this._syncing) return;
    const scrollEl = this.getScrollEl();
    if (!scrollEl) return;
    this._syncing = true;
    // Route through `scrollTable` (not `scrollEl.scrollLeft = …`) so the header
    // follows even while a filter popup blocks the holder's scroll event.
    this.scrollTable(this.proxyScrollEl.scrollLeft);
    this._syncing = false;
  }

  onTableScroll() {
    if (this._syncing) return;
    const scrollEl = this.getScrollEl();
    if (!scrollEl) return;
    this._syncing = true;
    this.proxyScrollEl.scrollLeft = scrollEl.scrollLeft;
    this._syncing = false;
  }

  // Match the proxy's scrollable width to the table's, hide the bar when
  // there's nothing to scroll, and keep the thumb aligned after a width
  // change (e.g. a resize mid-scroll).
  refresh() {
    const scrollEl = this.getScrollEl();
    if (!scrollEl) return;
    const { scrollWidth, clientWidth, scrollLeft } = scrollEl;
    this.spacer.style.width = `${scrollWidth}px`;
    // 1px slack absorbs sub-pixel rounding between the two elements' widths.
    const overflows = scrollWidth - clientWidth > 1;
    this.element.classList.toggle(HIDDEN_CLASS, !overflows);
    // Let SimpleBar re-measure the new content width and resize its thumb.
    this.simpleBar.recalculate();
    this._syncing = true;
    this.proxyScrollEl.scrollLeft = scrollLeft;
    this._syncing = false;
  }

  destroy() {
    this.proxyScrollEl.removeEventListener('scroll', this.onProxyScroll);
    const scrollEl = this.getScrollEl();
    if (scrollEl) scrollEl.removeEventListener('scroll', this.onTableScroll);
    this.resizeObserver?.disconnect();
    this.simpleBar.unMount();
    this.element.remove();
  }
}
